import { BookingDetails, BookingType } from '../domain/BookingDetails'
import { BookingService } from '../services/booking.service'
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ModelCloningService } from '../services/model-cloning.service'
import { NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap'
import { EMPTY, Observable, Subject, catchError, finalize, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs'
import { Availability, BookingSlotAreaGroup, BookingSlotChoice } from '../domain/Availability'
import { DurationUnit } from '../pipes/duration.pipe'
import { VenueStorefrontBookingSummary } from '../landing-page/components/venue-storefront-booking-summary-card/venue-storefront-booking-summary-card.component'
import { VenueStorefrontBookingSearch } from '../landing-page/components/venue-storefront-booking-flow/venue-storefront-booking-flow.types'
import { AvailabilityService } from '../services/availability.service'
import { Venue } from '../domain/Venue'
import { VenueService } from '../services/venue.service'
import { WindowService } from '../services/window/window.service'

@Component({
    selector: 'app-modify-booking',
    templateUrl: './modify-booking.component.html',
})
export class ModifyBookingComponent implements OnInit, OnDestroy {

    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string
    @Input() bookingId!: string
    form: FormGroup
    isLoading = false
    bookingContext$?: Observable<{ bookingDetails: BookingDetails; venue: Venue }>
    searchCriteria: VenueStorefrontBookingSearch | null = null
    availability: Availability | null = null
    areaGroups: BookingSlotAreaGroup[] | null = null
    currentSlot: BookingSlotChoice | null = null
    slotIdealTime: Date | null = null
    selectedSlot: BookingSlotChoice | null = null
    summary: VenueStorefrontBookingSummary | null = null
    errorMessage: string | null = null
    loadErrorMessage: string | null = null
    protected readonly DurationUnit = DurationUnit
    @ViewChild('reviewHeading') set reviewHeading(heading: ElementRef<HTMLElement> | undefined) {
        heading?.nativeElement.focus()
    }
    partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    private ngbDateAdapter = new NgbDateNativeAdapter()
    private onDestroy$ = new Subject<void>()

    constructor(
        private bookingService: BookingService,
        private fb: FormBuilder,
        private modelCloningService: ModelCloningService,
        private windowService: WindowService,
        private venueService: VenueService,
        private availabilityService: AvailabilityService
    ) {
        this.form = this.makeForm()
    }

    ngOnInit() {
        this.loadBookingDetails()
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    searchAvailability(search: VenueStorefrontBookingSearch, booking: BookingDetails, venue: Venue) {
        if (this.isLoading) {
            return
        }
        this.searchCriteria = {
            ...search,
            ...(booking.isDateRestricted ? { date: booking.dateTime, partySize: booking.partySize } : {}),
            requiresWheelchairAccess: booking.requiresWheelchairAccess,
            requiresDogFriendly: booking.requiresDogFriendly,
            requiresPramAccessible: booking.requiresPramAccessible,
            requiresHighchairFriendly: booking.requiresHighchairFriendly,
        }
        this.isLoading = true
        this.errorMessage = null
        this.availabilityService.getAvailability(
            venue.organisationId, venue.businessId, venue.id,
            this.searchCriteria.date, this.searchCriteria.date, booking.bookingId,
            booking.requiresWheelchairAccess, booking.requiresDogFriendly,
            booking.requiresPramAccessible, booking.requiresHighchairFriendly,
            booking.reasonIsLinkExclusive && booking.reasonId ? [booking.reasonId] : undefined
        ).pipe(
            takeUntil(this.onDestroy$),
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: availability => {
                this.availability = availability
                this.slotIdealTime = search.idealTime
                this.areaGroups = availability.bookingSlotAreaGroups(
                    this.searchCriteria!.partySize, booking.reasonId, booking.eventId,
                    booking.requiresDogFriendly, search.idealTime
                ).map(group => ({
                    ...group,
                    canJoinWaitlist: false,
                    slots: group.slots.map(slot => ({ ...slot, waitlistArea: null })),
                }))
                this.currentSlot = this.areaGroups.flatMap(group => group.slots).find(slot => {
                    return slot.bookingSlot.dateTime.getTime() === booking.dateTime.getTime()
                        && slot.reservableArea?.id === booking.areaId
                }) ?? null
            },
            error: () => this.errorMessage = 'We could not load availability. Please try again.',
        })
    }

    selectReplacement(slot: BookingSlotChoice, booking: BookingDetails, venue: Venue) {
        if (this.isLoading || !slot.reservableArea || !slot.reservableOption || !this.searchCriteria) {
            return
        }
        this.selectedSlot = slot
        const areaIsVisible = this.areaGroups?.some(group => group.area !== null)
        this.summary = {
            venueName: venue.displayName,
            venueImageUrl: null,
            partySize: this.searchCriteria.partySize,
            dateTime: slot.bookingSlot.dateTime,
            endDateTime: this.hasStorefrontChanges(booking)
                ? slot.bookingSlot.endDateTime(slot.reservableOption.durationMinutes) : booking.endTime,
            areaName: areaIsVisible ? slot.reservableArea.displayName : null,
            previous: {
                partySize: booking.partySize,
                dateTime: booking.dateTime,
                endDateTime: booking.endTime,
                areaName: booking.areaName,
            },
            currencyCode: venue.currencyCode,
            choice: { displayName: booking.eventName ?? booking.reasonName ?? 'Standard Booking' },
            extras: [],
        }
    }

    confirmStorefrontChanges(booking: BookingDetails) {
        const slot = this.selectedSlot
        if (this.isLoading || !slot?.reservableArea || !this.searchCriteria || !this.hasStorefrontChanges(booking)) {
            return
        }
        const updatedBooking = this.modelCloningService.cloneBookingDetails(booking)
        updatedBooking.dateTime = slot.bookingSlot.dateTime
        updatedBooking.areaId = slot.reservableArea.id
        updatedBooking.partySize = this.searchCriteria.partySize
        this.saveChanges(booking, updatedBooking)
    }

    hasStorefrontChanges(booking: BookingDetails): boolean {
        return !!this.selectedSlot?.reservableArea && !!this.searchCriteria && (
            this.selectedSlot.bookingSlot.dateTime.getTime() !== booking.dateTime.getTime()
            || this.selectedSlot.reservableArea.id !== booking.areaId
            || this.searchCriteria.partySize !== booking.partySize
        )
    }

    back() {
        if (this.isLoading) {
            return
        }
        if (this.selectedSlot) {
            this.currentSlot = this.selectedSlot
            this.slotIdealTime = this.selectedSlot.bookingSlot.dateTime
            this.selectedSlot = null
            this.summary = null
        } else {
            this.areaGroups = null
        }
        this.errorMessage = null
    }

    returnToBooking() {
        if (!this.isLoading) {
            this.windowService.navigateToUrl(`/booking/${this.bookingId}`)
        }
    }

    save(form: FormGroup, bookingDetails: BookingDetails) {
        if (form.invalid) {
            return
        }
        const day = this.ngbDateAdapter.toModel(form.get('date')!.value)
        if (!day) {
            return
        }
        const reasonId = form.get('selectedReasonId')!.value
        const areaId = form.get('selectedAreaId')!.value
        const partySize = form.get('partySize')!.value
        const time = form.get('time')!.value
        const dateTime = new Date(
            day.getFullYear(),
            day.getMonth(),
            day.getDate(),
            time.hour,
            time.minute
        )
        const updatedBookingDetails = this.modelCloningService.cloneBookingDetails(bookingDetails)
        updatedBookingDetails.dateTime = dateTime
        updatedBookingDetails.reasonId = reasonId
        updatedBookingDetails.areaId = areaId
        updatedBookingDetails.partySize = partySize
        this.saveChanges(bookingDetails, updatedBookingDetails)
    }

    private saveChanges(bookingDetails: BookingDetails, updatedBookingDetails: BookingDetails) {
        if (this.isLoading) {
            return
        }
        this.isLoading = true
        this.errorMessage = null
        this.bookingService.modifyBooking(
            this.organisationId,
            this.businessId,
            this.venueId,
            bookingDetails,
            updatedBookingDetails
        )
            .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: () => this.windowService.navigateToUrl(`/booking/${this.bookingId}`),
                error: () => {
                    this.errorMessage = 'We could not change your booking. Please try again or select another time.'
                },
            })
    }

    loadBookingDetails() {
        this.isLoading = true
        this.loadErrorMessage = null
        this.bookingContext$ = this.bookingService.getBookingDetails(this.bookingId)
            .pipe(
                switchMap(bookingDetails => {
                    if (!bookingDetails.modifyBookingPath) {
                        this.windowService.navigateToUrl(`/booking/${this.bookingId}`)
                        return EMPTY
                    }
                    return this.venueService.getWithId(bookingDetails.venueId).pipe(
                        map(venue => ({ bookingDetails, venue }))
                    )
                }),
                tap(({ bookingDetails }) => {
                    this.searchCriteria = {
                        partySize: bookingDetails.partySize,
                        date: bookingDetails.dateTime,
                        idealTime: bookingDetails.dateTime,
                        requiresWheelchairAccess: bookingDetails.requiresWheelchairAccess,
                        requiresDogFriendly: bookingDetails.requiresDogFriendly,
                        requiresPramAccessible: bookingDetails.requiresPramAccessible,
                        requiresHighchairFriendly: bookingDetails.requiresHighchairFriendly,
                        ...(bookingDetails.reasonIsLinkExclusive && bookingDetails.reasonId
                            ? { restrictedReasonIds: [bookingDetails.reasonId] } : {}),
                        ...(bookingDetails.eventId ? { eventId: bookingDetails.eventId } : {}),
                    }
                }),
                catchError(() => {
                    this.loadErrorMessage = 'We could not load your booking. Please try again.'
                    return EMPTY
                }),
                takeUntil(this.onDestroy$),
                finalize(() => this.isLoading = false),
                shareReplay({ bufferSize: 1, refCount: true })
            )
    }

    private makeForm() {
        return this.fb.group({
            'BookingSlot': this.fb.group({
                availability: [null, Validators.required],
                step: [null, Validators.required],
                partySize: [null, Validators.required],
                idealTime: [null, Validators.required],
                date: [null, Validators.required],
                requiresWheelchairAccess: [null],
                requiresDogFriendly: [null],
                requiresPramAccessible: [null],
                requiresHighchairFriendly: [null],
                selectedAreaId: [null, Validators.required],
                areaSelectionIsPossible: [true, Validators.required],
                bookingType: [BookingType.Standard, Validators.required],
                selectedEventId: [null],
                selectedReasonId: [null],
                time: [null, Validators.required],
                endTime: [null],
            }),
            'Order': this.fb.group({
                lineItems: new FormArray([]),
            }),
        })
    }
}
